import { AuthConfig } from 'angular-oauth2-oidc';
import { DateOnly } from './shared/date-only';

import { ResourceState, ResourceStatuses } from './shared/resource.state';

export interface SystemInfo {
  environment: string;
  version: string;
  informationalVersion: string;
}

export interface PortalConfig {
  allowedFileUploadFormatsInbox: string[];
  allowedFileUploadFormatsDocuments: string[];
  invalidFileAndFolderNameCharacters: string[];
  abacusInstances: string[];
  sharePointBaseUrl: string;
  sharePointInternalBaseUrl: string;
  windowsExplorerBaseUrl: string;
  maximumIdleTimeMinutes: number;
}

export type AuthorizePolicies =
  | 'CUSTOMER_SPACE_CREATE'
  | 'CUSTOMER_SPACE_READ'
  | 'CUSTOMER_SPACE_WRITE'
  | 'CUSTOMER_SPACE_DELETE'
  | 'COCKPIT_READ'
  | 'USERS_READ'
  | 'USERS_WRITE'
  | 'SHAREPOINT_TEMPLATE_WRITE'
  | 'REPORT_READ'
  | 'PORTAL_ADMIN'
  | 'PROJECT_CREATE'
  | 'PROJECT_READ'
  | 'PROJECT_WRITE'
  | 'PROJECT_DELETE'
  | 'APP_LINKS_READ'
  | 'APP_LINKS_ABACUS_READ'
  | 'APP_LINKS_ABACUS_ERP_READ'
  | 'APP_LINKS_ABACUS_OBT_ERP_READ'
  | 'APP_LINKS_TICKET_PORTAL_READ'
  | 'APP_LINKS_OBT_WEBSITE_READ'
  | 'APP_DOCUMENTS_READ'
  | 'APP_DOCUMENTS_WRITE'
  | 'APP_PROJECTS_READ'
  | 'APP_FILE_PROCESS_READ'
  | 'APP_FILE_PROCESS_WRITE'
  | 'FILE_PROCESS_READ'
  | 'FILE_PROCESS_WRITE'
  | 'FILE_PROCESS_POSITION_READ'
  | 'FILE_PROCESS_POSITION_WRITE'
  | 'FILE_PROCESS_REPORTING_READ'
  | 'FILE_PROCESS_REPORTING_WRITE'
  | 'FILE_PROCESS_ADDITIONAL_FOLDER_READ'
  | 'FILE_PROCESS_ADDITIONAL_FOLDER_WRITE'
  | 'APP_TASKS_READ'
  | 'APP_TASKS_WRITE'
  | 'APP_INBOX_UPLOAD_READ';

export type PermissionAction = 'read' | 'write';
export type UserLanguage = 'German' | 'English';

export interface AuthorizePolicy {
  name: AuthorizePolicies;
  permissions: RequiredPermission[];
}

export interface RequiredPermission {
  resourceId: string[];
  action: PermissionAction;
}

export interface PortalSettings {
  portalConfig: PortalConfig;
  systemInfo: SystemInfo;
  oidcConfig: AuthConfig;
  authorizePolicies: AuthorizePolicy[];
}

export interface DialogState {
  dialogs: DialogInfo[];
}

export interface DialogInfo {
  id: string;
  isOpen: boolean;
  data?: any;
}

export interface ProgressBarState {
  visible: boolean;
  progress: number;
  visibleProgress: number;
}

export interface IdentityClaims {
  sub: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  given_name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  family_name: string;
  upn: string;
}

export interface Permission {
  resourceId: string[];
  actions: string[];
}

export interface CurrentUser {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  mobileNumber: string;
  mail: string;
  organizationUnit: string;
  isInternal: boolean;
  isActive: boolean;
  language: UserLanguage;
  thumbnailPhoto: string;
  permissions: Permission[];
  activeUntil: DateOnly | null;
}

export interface AuthState {
  identityClaims: IdentityClaims;
  user: ResourceState<CurrentUser>;
}

export interface RouterState {
  lastPath: string | null;
  currentPath: string | null;
}

export interface PlatformState {
  auth: AuthState;
  portalSettings: ResourceState<PortalSettings>;
  dialog: DialogState;
  progressBar: ProgressBarState;
  router: RouterState;
}

export const initialPlatformState: PlatformState = {
  auth: {
    identityClaims: null!,
    user: {
      data: null!,
      error: null!,
      status: ResourceStatuses.NONE,
    },
  },
  portalSettings: {
    data: null!,
    error: null!,
    status: ResourceStatuses.NONE,
  },
  dialog: {
    dialogs: [],
  },
  progressBar: {
    visible: false,
    progress: 0,
    visibleProgress: 0,
  },
  router: {
    lastPath: null,
    currentPath: null,
  },
};

export const PLATFORM_STATE_FEATURE_KEY = 'platform';
